import { faArrowRight, faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, Table } from 'components';
import { ActiveDot } from 'components/ActiveDot/ActiveDot';
import { Button } from 'components/_form';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { IPlatformPractice } from 'types/platformPractice';
import { PriorityLevelBadge } from 'views/AdministrationView/views/TasksView/components/Priorities/components/PriorityLevelBadge/PriorityLevelBadge';

const Buttons = styled(Flex)`
  width: fit-content;
  margin: 0 auto;
  gap: 8px;
  align-items: center;
`;

const CenteredCell = styled(Flex)`
  justify-content: center;
  align-items: center;
  width: 100%;
`;

interface Props {
  practices: IPlatformPractice[];
  onEdit: (practice: IPlatformPractice) => void;
  onOpen?: (practice: IPlatformPractice) => void;
  hideMethodology?: boolean;
}

export const PlatformPracticesTable: React.FC<Props> = ({
  practices,
  onEdit,
  onOpen,
  hideMethodology
}) => {
  const { t } = useTranslation();

  const sortedPractices = useMemo(
    () =>
      [...practices].sort(
        (a, b) =>
          a.methodology.name.localeCompare(b.methodology.name) ||
          a.name.localeCompare(b.name)
      ),
    [practices]
  );

  const header = [
    t('platformPractices.tableHeaders.active'),
    t('platformPractices.tableHeaders.practice'),
    ...(hideMethodology ? [] : [t('platformPractices.tableHeaders.methodology')]),
    ''
  ];

  const data = useMemo(
    () =>
      sortedPractices.map((practice) => ({
        active: (
          <CenteredCell key={`active-${practice.id}`}>
            <ActiveDot active={practice.state === 'active'} />
          </CenteredCell>
        ),
        practice: onOpen ? (
          <Button
            key={`open-${practice.id}`}
            bordered
            transparent
            onClick={() => onOpen(practice)}
          >
            <PriorityLevelBadge priorityLevel={practice} />
          </Button>
        ) : (
          <PriorityLevelBadge key={`practice-${practice.id}`} priorityLevel={practice} />
        ),
        ...(hideMethodology
          ? {}
          : {
              methodology: (
                <span key={`methodology-${practice.id}`}>
                  {practice.methodology?.name || '—'}
                </span>
              )
            }),
        actions: (
          <Buttons key={`actions-${practice.id}`}>
            {onOpen && (
              <Button
                variant="grey"
                bordered
                transparent
                icon={<FontAwesomeIcon icon={faArrowRight} />}
                onClick={() => onOpen(practice)}
              />
            )}
            <Button
              variant="eucalyptus"
              icon={<FontAwesomeIcon icon={faPenToSquare} />}
              onClick={() => onEdit(practice)}
            />
          </Buttons>
        )
      })),
    [sortedPractices, onEdit, onOpen, hideMethodology]
  );

  return (
    <Table
      header={header}
      data={data}
      sortTable={false}
      leftFixedKeys={[1]}
      rightFixedKeys={[header.length - 1]}
      centeredColumnKeys={[0]}
    />
  );
};
